import React, { useState, useEffect, useCallback } from "react";
import { X, RefreshCcw } from "lucide-react";
import ChatLibraryPanel from "./ChatLibraryPanel";
import ChatMessagesPanel from "./ChatMessagesPanel";
import CsvUploadPanel from "./CsvUploadPanel";
import { mergeConversationMessages } from "./MergeCovesatition";

import {
  uploadCsvApi,
  getSessionId,
  askAiApi,
  getSessionsApi,
  getConversationByIdApi,
} from "../PerformanceTracker/actioncreator";

import "./index.scss";


const ChatWidget = ({ 
  onClose, 
  uploadedFile: 
  initialFile,
  admin ,
  isPreUploaded,
  isPreSessionId,
  showChatFromPreupload,
   reportQuestions = [],
  selectedReport,
  predefined,
 }) => {
  

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [uploadedFile, setUploadedFile] = useState( initialFile || isPreUploaded || showChatFromPreupload,);

  const [sessionId, setSessionId] = useState("");
  const [sessions, setSessions] = useState([]);

  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);

  const [showSuggestions, setShowSuggestions] = useState(true);
  const [isSending, setIsSending] = useState(false);

   useEffect(() => {
    if (isPreSessionId) {
      setSessionId(isPreSessionId);
    }
  }, [isPreSessionId]);

  useEffect(() => {
    if (messages.length > 0) {
    }
  }, [messages]);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      
      const data = await getSessionsApi(admin?.emailId);

      setSessions(data.sessions || []);
    } catch (error) {
      console.error("Failed to load sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const openConversation = async (session_id) => {
    try {
      setSessionId(session_id);

      setMessages([]);

      setLoadingConversation(true);

      const data = await getConversationByIdApi(session_id || sessionId, admin?.emailId);

      const currentSession = sessions.find((s) => s.session_id === session_id);
      const fileName =
        data.filename || currentSession?.filename || "Unknown File";

      setUploadedFile({ name: fileName });

      if (!data.messages || data.messages.length === 0) {
        setMessages([
          {
            author: "assistant",
            type: "answer",
            text: "No messages in this conversation yet. Start chatting!",
            time: new Date().toISOString(),
          },
        ]);
        return;
      }

      const merged = mergeConversationMessages(data?.messages?.reverse() || []);
      setMessages(merged);
    } catch (error) {
      console.error("Failed to load conversation:", error);
      setMessages([
        {
          author: "assistant",
          type: "answer",
          text: "Failed to load conversation. Please try again.",
          time: new Date().toISOString(),
        },
      ]);
      setUploadedFile(null);
    } finally {
      setLoadingConversation(false);
    }
  };

   const sendMessage = useCallback(
    (text) => {
      if (!text.trim() || isSending) return;
      setShowSuggestions(true);
      setIsSending(true);

      setMessages((prev) => [
        ...prev,
        { author: "user", text, time: new Date().toISOString() },
        { id: "loader", author: "assistant", type: "loader" },
      ]);

      if (!sessionId) {
        alert("Please upload a file first.");
        setIsSending(false);
        setMessages((prev) => prev.filter((m) => m.id !== "loader"));
        return;
      }                                                                                                     
      askAiApi(text, sessionId, admin?.emailId)
        .then((stream) => {
          const answerRef = { current: "" };
          const debugRef = { current: "" };
          const isAnswerModeRef = { current: false };
          const chartsRef = { current: [] };

          setMessages((prev) =>
            prev
              .filter((m) => m.id !== "loader")
              .concat({
                id: "answer-" + Date.now(),
                author: "assistant",
                type: "answer",
                text: "",
                debug: "",
                charts: [],
                isComplete: false,
                time: new Date().toISOString(),
              }),
          );

          (async () => {
            try {
              for await (const chunk of stream) {
                await new Promise((r) => setTimeout(r, 20));
                if (chunk.type === "answer") {
                  if (chunk.content.trim() === "???") {
                    isAnswerModeRef.current = true;
                    continue;
                  }

                  if (!isAnswerModeRef.current) {
                    debugRef.current += chunk.content;
                  } else {
                    answerRef.current += chunk.content;
                  }

                  setMessages((prev) => {
                    const updated = [...prev];
                    const lastMsg = updated[updated.length - 1];
                    if (!lastMsg) return prev;

                    if (!isAnswerModeRef.current) {
                      lastMsg.debug = debugRef.current;
                    } else {
                      lastMsg.text = answerRef.current;
                    }
                    return updated;
                  });
                }

                if (chunk.type === "chart") {
                  chartsRef.current.push({
                    title: chunk.title,
                    image: chunk.data,
                    isFile: false,
                  });
                }

                if (chunk.type === "file") {
                  chartsRef.current.push({
                    isFile: true,
                    filename: chunk.filename,
                    format: chunk.format,
                    data: chunk.data,
                  });
                }
              }

              setMessages((prev) => {
                const updated = [...prev];
                const lastMsg = updated[updated.length - 1];
                if (lastMsg) {
                  lastMsg.text = answerRef.current;
                  lastMsg.debug = debugRef.current;
                  lastMsg.charts = chartsRef.current;
                  lastMsg.isComplete = true;
                }
                return updated;
              });

              loadSessions();
            } catch (streamError) {
              console.error("Stream processing error:", streamError);
              setMessages((prev) => {
                const updated = [...prev];
                const lastMsg = updated[updated.length - 1];
                if (lastMsg && !lastMsg.isComplete) {
                  lastMsg.text =
                    answerRef.current ||
                    "Stream error occurred. Use retry button.";
                  lastMsg.isComplete = true;
                  lastMsg.hasError = true;
                }
                return updated;
              });
            } finally {
              setIsSending(false);
            }
          })();
        })
        .catch((error) => {
          console.error("Message send error:", error);
          setMessages((prev) => prev.filter((m) => m.id !== "loader"));
          setMessages((prev) => [
            ...prev,
            {
              author: "assistant",
              type: "answer",
              text: "Something went wrong. You can retry this message.",
              time: new Date().toISOString(),
              isComplete: true,
              hasError: true,
            },
          ]);
          setIsSending(false);
        });
    },
    [sessionId, isSending],
  );

  const send = () => {
    if (!input.trim()) return;
    const userText = input;
    setInput("");
    sendMessage(userText);
  };

  const handleFileUpload = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith(".csv")) {
      alert("Please upload a valid CSV file");
      return;
    }

    const newSessionId = crypto.randomUUID();
    // window.sessionStorage.setItem("currentSessionId", newSessionId);
    setSessionId(newSessionId);

    setMessages([]);

    try {
      const response = await uploadCsvApi(file, newSessionId,admin.emailId);

      setUploadedFile(file);

      setMessages([
        {
          author: "assistant",
          type: "upload-success",
          text: `File "${file.name}" uploaded successfully!`,
        },
        {
          author: "assistant",
          type: "upload-preview",
          columns: response.columns,
          preview: response.preview,
        },
      ]);

      loadSessions();
    } catch (error) {
      setMessages([
        {
          author: "assistant",
          type: "answer",
          text: `Upload failed\n${error.message}`,
        },
      ]);
    }
  };

 useEffect(() => {
    if (isPreUploaded && showChatFromPreupload) {
      setMessages([
        {
          author: "assistant",
          type: "upload-success",
          text: `File uploaded successfully!`,
        },
        {
          author: "assistant",
          type: "upload-preview",
          columns: showChatFromPreupload?.columns,
          preview: showChatFromPreupload?.preview,
        },
      ]);
    }
  }, [isPreUploaded, showChatFromPreupload]);

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setSessionId("");
    setMessages([]);
    sessionStorage.removeItem("lastPreviewReportUrl");
    sessionStorage.removeItem("selectedReportName");
    // setUploadedFile(false);
  };

  
  return (
    <>
      <div className="ai-backdrop" onClick={onClose} />

      <div className="sb-chat fullscreen">
        <div className="sb-header">
          <div className="sb-title">
            <div className="sb-icon">AI</div>
            <div className="title-rep">
              <strong>Reports Bot</strong>
              <div className="sb-status">
                <span className="dot" /> Online
              </div>
            </div>
          </div>

          <div className="sb-actions">
            <RefreshCcw
              size={18}
              onClick={handleRemoveFile}
              style={{ cursor: "pointer" }}
            />
            <X size={18} onClick={()=>{
              onClose();
              sessionStorage.removeItem("lastPreviewReportUrl");
    sessionStorage.removeItem("selectedReportName");

            }} />
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          <ChatLibraryPanel
            sessions={sessions}
            loading={loadingSessions}
            openConversation={openConversation}
            activeSessionId={sessionId}
          />

          <ChatMessagesPanel
            messages={messages}
            input={input}
            setInput={setInput}
            send={send}
            uploadedFile={uploadedFile}
            loadingConversation={loadingConversation}
            isSending={isSending}
            sendMessage={sendMessage}
            reportQuestions={reportQuestions}
            showSuggestions={showSuggestions}
          />

          <CsvUploadPanel
            onFileUpload={handleFileUpload}
            uploadedFile={uploadedFile}
            onRemoveFile={handleRemoveFile}
          />
        </div>
      </div>
    </>
  );
};

export default ChatWidget;
